<template>
  <div class="page-container page-editor">
    <div class="page-title-container d-flex align-items-center">
      <h1 class="page-title">
        {{ editing ? "Editar página" : "Nueva página" }}
      </h1>
      <span class="badge badge-blue ml-3" v-if="page.isSystemPage">Página del sistema</span>
    </div>
    <div class="container px-0">
      <div class="row justify-content-center">
        <div class="col-12">
          <b-card
            tag="article"
            no-body
            class="custom-card"
          >
            <b-card-header
              header-tag="section"
              class="custom-card-header d-flex align-items-center"
            >
              <h3>
                {{
                  editing ? "Datos de la página" : "Datos de la nueva página"
                }}
              </h3>
            </b-card-header>
            <b-card-body class="custom-card-body px-0 pb-0">
              <form class="custom-form" @submit.prevent="save" novalidate>
                <div class="form-section pt-0">
                  <h2 class="custom-card-subtitle">Información general</h2>
                  <p class="custom-card-description">
                    Ingresa los datos
                    {{ editing ? "de la página" : "de la nueva página" }}
                  </p>

                  <div class="row">
                    <div class="col-12">
                      <div class="form-group">
                        <label for="name">Título de la página *</label>

                        <FormInput
                          id="name"
                          type="text"
                          placeholder="Ingresar título"
                          v-model="page.name"
                          :show-error="$v.page.name.$error"
                        />
                        <form-error
                          message="El título es obligatorio"
                          v-if="$v.page.name.$error && !$v.page.name.required"
                        ></form-error>
                      </div>

                      <div class="form-group">
                        <label for="slug">Slug *</label>

                        <FormInput
                          id="slug"
                          type="text"
                          placeholder="Ingresar slug"
                          v-model="page.slug"
                          :disabled="page.isSystemPage"
                          :show-error="$v.page.slug.$error"
                        />
                        <form-error
                          message="El slug es obligatorio"
                          v-if="$v.page.slug.$error && !$v.page.slug.required"
                        ></form-error>
                        <small class="form-text text-muted" v-if="page.isSystemPage">
                          La tienda enlaza esta página desde el pie de página, por eso su URL no se puede cambiar.
                        </small>
                        <small class="form-text text-muted" v-else-if="slugChanged">
                          Al guardar se creará una redirección 301 de /{{ originalSlug }} a /{{ normalizedSlug }}
                          (Herramientas &gt; Redirecciones).
                        </small>
                        <small class="form-text text-muted" v-else-if="normalizedSlug">
                          {{ webUrl }}/{{ normalizedSlug }}
                        </small>
                      </div>

                      <div class="form-group">
                        <label for="status">Estado *</label>

                        <FormSelect
                          id="status"
                          v-model="page.status"
                          :items="statusOptions"
                          :disabled="page.isSystemPage"
                        />
                        <small class="form-text text-muted">
                          Las páginas en borrador no se muestran en la tienda.
                        </small>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-12">
                      <div class="form-group">
                        <label for="description">Descripción</label>
                        <global-trumbowyg v-model="page.description"/>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-section">
                  <h2 class="custom-card-subtitle">Imagen destacada</h2>

                  <p class="custom-card-description">
                    Opcional. Se muestra en la cabecera de la página y al compartirla en redes sociales.
                  </p>

                  <UploadImages
                    :maxFiles="1"
                    :files="oldFiles"
                    @onChange="onChangeImage"
                    @remove="removeImage"
                  />
                </div>

                <div class="form-section">
                  <h2 class="custom-card-subtitle">Datos SEO</h2>

                  <p class="custom-card-description">
                    Ingresa los siguientes datos SEO
                  </p>

                  <div class="row">
                    <div class="col-md-12">
                      <div class="form-group">
                        <label for="seoTitle">Título SEO ({{ page.seoTitle ? page.seoTitle.length : 0 }})</label>
                        <FormInput
                          id="seoTitle"
                          type="text"
                          placeholder="Ingresar título SEO"
                          v-model="page.seoTitle"
                        />
                        <form-error
                          message="El título SEO puede tener hasta 70 caracteres"
                            v-if="$v.page.seoTitle.$error && !$v.page.seoTitle.maxLength"
                        ></form-error>
                      </div>
                    </div>

                    <div class="col-md-12">
                      <div class="form-group">
                        <label for="seoKeywords">Palabras Clave</label>
                        <FormInput
                          id="seoKeywords"
                          type="text"
                          placeholder="Ingresar palabras clave"
                          v-model="page.seoKeywords"
                        />
                      </div>
                    </div>

                    <div class="col-md-12">
                      <div class="form-group mb-0">
                        <label for="seoMeta">Meta description ({{ page.seoMeta ? page.seoMeta.length : 0 }})</label>
                        <textarea
                          id="seoMeta"
                          placeholder="Escriba aquí"
                          class="custom-form-control"
                          v-model="page.seoMeta"
                        ></textarea>
                        <form-error
                        message="El meta description puede tener hasta 320 caracteres"
                        v-if="$v.page.seoMeta.$error && !$v.page.seoMeta.maxLength"
                        ></form-error>
                      </div>
                    </div>

                    <div class="form-group-buttons d-flex justify-content-between align-items-center w-100">
                      <div>
                        <button
                          type="button"
                          class="button button-light text-danger"
                          @click="openDelete"
                          v-if="editing && !page.isSystemPage"
                        >
                          Eliminar
                        </button>
                      </div>
                      <div class="text-right">
                        <router-link
                          :to="{ name: 'pages-list' }"
                          class="button button-light"
                        >
                          {{ editing ? "Volver" : "Cancelar" }}
                        </router-link>
                        <button class="button button-primary" type="submit">
                          Guardar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import {
  Alert,
  FormInput,
  FormSelect,
  StringUtil,
  Constants,
  FormError,
  UploadImages,
  ConfirmationModal,
  ConfirmationModalMixin,
} from "@wizepe/wize-admin";

import { required, maxLength } from "vuelidate/lib/validators";

import { PostService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";
import UploadImagesMixin from "@/core/mixins/UploadImagesMixin";
import GlobalTrumbowyg from "@/core/components/common/GlobalTrumbowyg";

export default {
  mixins: [ConfirmationModalMixin, UploadImagesMixin],
  data() {
    return {
      page: {
        name: null,
        slug: null,
        status: LocalConstants.PostStatus.PUBLISHED.key,
        isSystemPage: false,
        featuredImage: null,
        featuredImageAssetId: null,
        description: null,
        seoTitle: null,
        seoKeywords: null,
        seoMeta: null,
      },
      originalSlug: null,
      editing: false,
      webUrl: process.env.VUE_APP_WEB_URL,
      statusOptions: [
        { key: LocalConstants.PostStatus.PUBLISHED.key, label: "Publicado" },
        { key: LocalConstants.PostStatus.DRAFT.key, label: "Borrador" },
      ],
    };
  },
  components: {
    FormInput,
    FormSelect,
    GlobalTrumbowyg,
    FormError,
    UploadImages,
    ConfirmationModal,
  },
  validations: {
    page: {
      name: { required },
      slug: { required },
      seoTitle: {maxLength: maxLength(70)},
      seoMeta: {maxLength: maxLength(320)}
    },
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    "page.name": function (val) {
      if (val && !this.editing) {
        this.page.slug = StringUtil.toSlug(val);
      }
    },
  },
  computed: {
    normalizedSlug() {
      return this.page.slug ? StringUtil.toSlug(this.page.slug) : "";
    },
    slugChanged() {
      return this.editing && this.originalSlug && this.normalizedSlug
        && this.normalizedSlug !== this.originalSlug;
    },
    oldFiles() {
      return this.page.featuredImage ? [this.page.featuredImage] : [];
    },
  },
  methods: {
    async onChangeImage(files) {
      try {
        const uploadedFiles = await this.uploadFiles(files);
        if (uploadedFiles && uploadedFiles.length) {
          this.page.featuredImageAssetId = uploadedFiles[0].id;
        }
      } catch (error) {
        console.error(error);
        Alert.error("No se pudo subir la imagen");
      }
    },
    removeImage() {
      this.page.featuredImage = null;
      this.page.featuredImageAssetId = null;
    },
    async save() {
      this.$v.$touch();

      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      this.$store.dispatch("app/loading", true);

      const data = {
        name: this.page.name,
        slug: this.normalizedSlug,
        status: this.page.status,
        featuredImageAssetId: this.page.featuredImageAssetId,
        description: this.page.description,
        seoTitle: this.page.seoTitle ? this.page.seoTitle.trim() : null,
        seoKeywords: this.page.seoKeywords,
        seoMeta: this.page.seoMeta ? this.page.seoMeta.trim() : null,
      };

      try {
        let response;
        if (!this.editing) response = await PostService.create(data);
        else response = await PostService.update(this.$route.params.id, data);

        Alert.success("Se almacenó la información correctamente");
        if (!this.editing) {
          this.$router.push({
            name: "pages-edit",
            params: { id: response.payload.id },
          });
        } else {
          this.setPage(response.payload);
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    setPage(payload) {
      this.page = {
        ...payload,
        featuredImageAssetId: payload.featuredImage ? payload.featuredImage.id : null,
      };
      this.originalSlug = payload.slug;
      this.editing = true;
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await PostService.get(id);
        this.setPage(response.payload);
      } catch (ex) {
        let status = ex.status;

        if (status == "NOT_FOUND") {
          let message = Constants.Errors.RECORD_NOT_FOUND;

          Alert.error(message, () =>
            this.$router.push({ name: "pages-list" })
          );
        } else {
          Alert.error(ex.errors ? ex.errors.message : ex);
        }
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    openDelete() {
      this.openConfirmationModal({
        title: "Eliminar página",
        description: `¿Está seguro que desea eliminar la página "${this.page.name}"? Esta acción no se puede deshacer.`,
        acceptText: "Eliminar",
        action: "delete",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "delete") this.deletePage();
    },
    async deletePage() {
      try {
        await this.$store.dispatch("app/loading", true);
        await PostService.delete(this.$route.params.id);
        Alert.success("Página eliminada");
        this.$router.push({ name: "pages-list" });
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
  },
  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
